// @ts-nocheck
import { GoogleGenAI, Modality } from "@google/genai";

// Initialize the client with the API key from environment variables
// Note: In a real production app, you might want to proxy this through a backend
// to keep the key secure, or use the user's key if it's a BYOK app.
const ai = new GoogleGenAI({ apiKey: (((globalThis as any).__env)?.GEMINI_API_KEY) || '' });

export const generateText = async (prompt: string, model = "gemini-3.1-flash-lite-preview") => {
  try {
    const response = await ai.models.generateContent({
      model: model,
      contents: prompt,
    });
    return response.text;
  } catch (error) {
    console.error("Error generating text:", error);
    throw error;
  }
};

// Helper to write WAV header
const writeWavHeader = (samples: Uint8Array, sampleRate: number, numChannels: number, bitDepth: number) => {
  const buffer = new ArrayBuffer(44 + samples.length);
  const view = new DataView(buffer);

  // RIFF identifier
  writeString(view, 0, 'RIFF');
  // file length
  view.setUint32(4, 36 + samples.length, true);
  // RIFF type
  writeString(view, 8, 'WAVE');
  // format chunk identifier
  writeString(view, 12, 'fmt ');
  // format chunk length
  view.setUint32(16, 16, true);
  // sample format (raw)
  view.setUint16(20, 1, true);
  // channel count
  view.setUint16(22, numChannels, true);
  // sample rate
  view.setUint32(24, sampleRate, true);
  // byte rate (sample rate * block align)
  view.setUint32(28, sampleRate * numChannels * (bitDepth / 8), true);
  // block align (channel count * bytes per sample)
  view.setUint16(32, numChannels * (bitDepth / 8), true);
  // bits per sample
  view.setUint16(34, bitDepth, true);
  // data chunk identifier
  writeString(view, 36, 'data');
  // data chunk length
  view.setUint32(40, samples.length, true);

  // write the PCM samples
  const pcmData = new Uint8Array(buffer, 44);
  pcmData.set(samples);

  return buffer;
};

const writeString = (view: DataView, offset: number, string: string) => {
  for (let i = 0; i < string.length; i++) {
    view.setUint8(offset + i, string.charCodeAt(i));
  }
};

export const generateSpeech = async (text: string, voiceName: string = 'kore') => {
  try {
    const response = await ai.models.generateContent({
      model: "gemini-2.5-flash-preview-tts",
      contents: [{ parts: [{ text }] }],
      config: {
        responseModalities: [Modality.AUDIO],
        speechConfig: {
          voiceConfig: {
            prebuiltVoiceConfig: { voiceName },
          },
        },
      },
    });

    const base64Audio = response.candidates?.[0]?.content?.parts?.[0]?.inlineData?.data;
    if (!base64Audio) {
      throw new Error("No audio data returned");
    }
    
    // Convert base64 to byte array
    const binaryString = window.atob(base64Audio);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }

    // Add WAV header (24kHz, 1 channel, 16-bit is typical for this model)
    const wavBuffer = writeWavHeader(bytes, 24000, 1, 16);
    return new Blob([wavBuffer], { type: 'audio/wav' });

  } catch (error) {
    console.error("Error generating speech:", error);
    throw error;
  }
};

export const generateMusic = async (prompt: string, parameters: Record<string, unknown>) => {
  try {
    // Construct the Lyria-compliant JSON structure
    const lyriaConfig = {
      prompts: [
        { text: prompt, weight: 1.0 },
        ...((parameters.instruments as string[]) || []).map((inst: string) => ({ text: inst, weight: 0.8 })),
        ...((parameters.genres as string[]) || []).map((genre: string) => ({ text: genre, weight: 0.8 })),
        ...((parameters.moods as string[]) || []).map((mood: string) => ({ text: mood, weight: 0.8 }))
      ],
      config: {
        bpm: parameters.bpm || 120,
        scale: parameters.key !== 'Unspecified' ? parameters.key : undefined,
        music_generation_mode: 'QUALITY'
      }
    };

    // Use the correct native audio model
    const response = await ai.models.generateContent({
      model: "gemini-2.5-flash-native-audio-preview-09-2025", 
      contents: `Generate instrumental music based on this configuration: ${JSON.stringify(lyriaConfig)}`,
      config: {
        responseModalities: [Modality.AUDIO],
      },
    });

    const base64Audio = response.candidates?.[0]?.content?.parts?.[0]?.inlineData?.data;
    if (!base64Audio) {
      throw new Error("No audio data returned");
    }

    // Convert base64 to byte array
    const binaryString = window.atob(base64Audio);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }

    // Add WAV header (44.1kHz or 48kHz usually for music, assuming 44100Hz, 1 channel for now to be safe, or 2 if stereo)
    // Native audio model often returns 24kHz or 44.1kHz. Let's try 24000 first as it matches the speech model often used in these previews.
    // If it sounds slow/fast, we adjust this.
    const wavBuffer = writeWavHeader(bytes, 24000, 1, 16);
    return new Blob([wavBuffer], { type: 'audio/wav' }); 

  } catch (error) {
    console.error("Error generating music:", error);
    throw error;
  }
};

export const generateImage = async (prompt: string, options?: { aspectRatio?: string, referenceImage?: string }) => {
  try {
    const apiKey = (((globalThis as any).__env)?.API_KEY) || (((globalThis as any).__env)?.GEMINI_API_KEY) || '';
    if (!apiKey) {
        // Fallback for demo if no key
        console.warn("No API key for image generation, returning placeholder.");
        return "https://picsum.photos/1024/1024";
    }
    
    const client = new GoogleGenAI({ apiKey });
    
    const parts: Record<string, unknown>[] = [];
    
    // Add reference image if provided
    if (options?.referenceImage) {
        // Remove data:image/png;base64, prefix if present
        const base64Data = options.referenceImage.replace(/^data:image\/\w+;base64,/, "");
        parts.push({
            inlineData: {
                data: base64Data,
                mimeType: "image/png"
            }
        });
    }
    
    parts.push({ text: prompt });

    // Use gemini-3-pro-image-preview as requested
    const response = await client.models.generateContent({
        model: 'gemini-3-pro-image-preview',
        contents: {
            parts: parts
        },
        config: {
            imageConfig: options?.aspectRatio ? { aspectRatio: options.aspectRatio, imageSize: "1K" } : { imageSize: "1K" }
        }
    });

    if (response.candidates && response.candidates[0].content.parts) {
        for (const part of response.candidates[0].content.parts) {
            if (part.inlineData) {
                return `data:${part.inlineData.mimeType};base64,${part.inlineData.data}`;
            }
        }
    }
    
    // If no inline data, check if it returned a text url (unlikely for this model but possible in some configs)
    if (response.text && response.text.startsWith('http')) {
        return response.text;
    }

    throw new Error("No image data returned from API");

  } catch (error) {
    console.error("Error generating image:", error);
    // Fallback
    return "https://picsum.photos/1024/1024";
  }
};

export const generateVideo = async (prompt: string, options?: {
  image?: string; // Base64
  lastFrame?: string; // Base64
  referenceImages?: string[]; // Array of Base64
  apiKey?: string;
  resolution?: '720p' | '1080p' | '4K';
  aspectRatio?: string;
  model?: string;
}) => {
  try {
    const apiKey = options?.apiKey || (((globalThis as any).__env)?.API_KEY) || (((globalThis as any).__env)?.GEMINI_API_KEY) || '';
    if (!apiKey) {
        throw new Error("No API key found. Please select a paid API key to use Veo.");
    }
    const client = new GoogleGenAI({ apiKey });
    
    console.log("Starting video generation with Veo...");
    
    let model = options?.model || 'veo-3.1-generate-preview';
    const config: Record<string, unknown> = {
      numberOfVideos: 1,
      resolution: options?.resolution || '1080p',
      aspectRatio: options?.aspectRatio || '16:9'
    };

    // Determine model and config based on options
    if (options?.referenceImages && options.referenceImages.length > 0) {
        if (!options?.model) model = 'veo-3.1-generate-preview'; // Required for reference images
        config.referenceImages = options.referenceImages.map(img => ({
            image: { imageBytes: img, mimeType: 'image/png' },
            referenceType: 'ASSET' // Assuming ASSET for now
        }));
    }

    if (options?.lastFrame) {
        config.lastFrame = {
            imageBytes: options.lastFrame,
            mimeType: 'image/png'
        };
    }

    const request: Record<string, unknown> = {
        model,
        prompt,
        config
    };

    if (options?.image) {
        request.image = {
            imageBytes: options.image,
            mimeType: 'image/png'
        };
    }

    console.log("Video Generation Request:", { 
        model, 
        prompt, 
        config, 
        hasImage: !!options?.image,
        hasReferenceImages: !!options?.referenceImages
    });

    let operation = await client.models.generateVideos(request);

    console.log("Video generation started, polling for completion...");
    while (!operation.done) {
      await new Promise(resolve => setTimeout(resolve, 5000));
      // Use the correct parameter structure as per documentation
      operation = await client.operations.getVideosOperation({ operation: operation });
      console.log("Polling status:", operation.metadata?.state);
    }

    if (operation.error) {
        console.error("Video Generation Operation Error:", operation.error);
        throw new Error(`Video generation failed: ${operation.error.message}`);
    }

    const videoResult = operation.response?.generatedVideos?.[0]?.video;
    const videoUri = videoResult?.uri;
    
    if (!videoUri) {
        throw new Error("No video URI returned from generation.");
    }

    const videoUrl = `${videoUri}&key=${apiKey}`;
    
    return { url: videoUrl };

  } catch (error) {
    console.error("Error generating video:", error);
    throw error;
  }
};
